import { useEffect, useState } from "react";
import { Radio } from "lucide-react";
import api from "@/lib/api";

/**
 * Real-time "who's on the site right now" pill for staff.
 * Polls /admin/presence/live every 15s.
 * "Online" = seen within the last 90 s (server-defined window).
 */
export default function LiveNow() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get("/admin/presence/live");
        if (!cancelled) {
          setData(data);
          setErr(null);
        }
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || "Failed");
      }
    };
    load();
    const t = setInterval(load, 15_000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, []);

  if (err) return null; // silent — non-critical widget

  const total = data?.total ?? "—";
  const logged = data?.logged_in ?? 0;
  const anon = data?.anonymous ?? 0;
  const paths = data?.by_path || [];

  return (
    <div
      data-testid="live-now-card"
      className="rounded-2xl border border-emerald-500/25 bg-emerald-500/[0.04] p-5"
    >
      <div className="flex items-center gap-2">
        <span className="relative inline-flex h-2.5 w-2.5">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
        </span>
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-300 inline-flex items-center gap-1.5">
          <Radio className="w-3.5 h-3.5" /> Live now
        </span>
      </div>

      <div className="mt-3 flex items-baseline gap-3 flex-wrap">
        <div
          data-testid="live-now-total"
          className="font-heading text-4xl font-black tabular-nums text-white"
        >
          {total}
        </div>
        <div className="text-xs text-zinc-400">
          people on the site in the last 90 s
        </div>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
        <div className="rounded-lg bg-white/5 border border-white/10 px-3 py-2">
          <div className="text-zinc-500 uppercase tracking-widest text-[10px] font-bold">
            Logged in
          </div>
          <div className="font-heading text-lg font-black tabular-nums text-white">
            {logged}
          </div>
        </div>
        <div className="rounded-lg bg-white/5 border border-white/10 px-3 py-2">
          <div className="text-zinc-500 uppercase tracking-widest text-[10px] font-bold">
            Anonymous
          </div>
          <div className="font-heading text-lg font-black tabular-nums text-white">
            {anon}
          </div>
        </div>
      </div>

      {paths.length > 0 && (
        <div className="mt-4">
          <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-2">
            Top pages right now
          </div>
          <ul className="space-y-1">
            {paths.slice(0, 6).map((p) => (
              <li
                key={p.path}
                className="flex items-center justify-between gap-2 text-xs"
              >
                <span className="text-zinc-300 font-mono truncate min-w-0">
                  {p.path || "/"}
                </span>
                <span className="text-emerald-300 font-bold tabular-nums shrink-0">
                  {p.count}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
